/**
 * Outbound candidate mail from the TA Action Board: sending as the signed-in
 * recruiter, and the record of what has been sent (the Log page).
 *
 * Split out of taActionBoardApi.ts to keep that file under the 500-line limit.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { BryqKey } from './bryqApi';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

export interface SentEmail {
  opportunityId: string;
  candidateName: string | null;
  to: string;
  subject: string;
  kind: string;
  roleTitle: string | null;
  stageName: string | null;
  attachmentName: string | null;
  attachmentBytes: number | null;
  sentBy: string;
  sentAt: string;
}

/** What the board has emailed candidates — the Log page's second table. */
export async function fetchSentEmails(): Promise<SentEmail[]> {
  const res = await authenticatedFetch(`${BASE}/sent-emails`, { cache: 'no-store' });
  const data = (await res.json()) as { sent?: SentEmail[] };
  return Array.isArray(data.sent) ? data.sent : [];
}

/**
 * Send the recruiter-authored candidate email AS THE SIGNED-IN USER. The proxy
 * resolves the recipient from Lever — it is never taken from here.
 */
export async function sendCandidateEmail(
  payload: {
    opportunityId: string;
    subject: string;
    body: string;
    kind?: 'case-invite' | 'move-invite' | 'first-invite' | 'fr-invite' | 'link-resend';
    roleTitle?: string;
    stageName?: string;
    /**
     * Role + stage IDS. The board reads its own sent-email log to decide who has
     * already been invited, and keys on (opportunity, stage) — a stage NAME is
     * renameable in Lever, which would silently start re-offering invites.
     */
    postingId?: string;
    stageId?: string;
    attachment?: { filename: string; mimeType: string; contentBase64: string } | null;
    /** A Bryq test to send right after this email went out ("Bryq with the invite"). */
    bryq?: BryqKey | null;
  },
  gmailToken: string | null
): Promise<{
  ok: boolean;
  to: string;
  attachmentName: string | null;
  sentAt: string;
  /** null when no Bryq was asked for; otherwise whether that step worked. */
  bryq?: { ok: boolean; label?: string; error?: string } | null;
}> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (gmailToken) headers['X-Gmail-Token'] = gmailToken;
  const res = await authenticatedFetch(`${BASE}/send-email`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  return res.json();
}

export interface DecisionLogEntry {
  opportunityId: string;
  decision: 'rejected' | 'advanced' | string;
  candidateName?: string | null;
  stageName?: string | null;
  movedTo?: string | null;
  archiveReason?: string | null;
  rating: string | null;
  /** INTERNAL — the interviewers' own points. Never send this to a candidate. */
  internalSummary: string[];
  /** AGG-filtered paragraph, safe to send if they ask for detail. */
  candidateFeedback: string | null;
  decidedBy: string;
  decidedAt: string;
}

/** Interview decisions for the Log page (six-month retention, server-enforced). */
export async function fetchDecisionLog(): Promise<DecisionLogEntry[]> {
  const res = await authenticatedFetch(`${BASE}/decision-log`, { cache: 'no-store' });
  const data = (await res.json()) as { decisions?: DecisionLogEntry[] };
  return Array.isArray(data.decisions) ? data.decisions : [];
}

export interface ScreeningEvent {
  /** archived | sent | failed */
  event: string;
  opportunityID: string;
  roleTitle: string | null;
  occurredAt: string;
  // Present only on `failed` events:
  reason?: string;
  candidateName?: string | null;
  candidateEmail?: string | null;
  templateKey?: string | null;
  language?: string | null;
  senderEmail?: string | null;
  detail?: string | null;
  attempts?: number | null;
}

/**
 * Everything the Make screening-rejection automation reported: archived, sent and
 * failed events. The Screening Log tab counts archived/sent by role and lists the
 * failures in full. Newest first, six-month retention (server-enforced).
 */
export async function fetchScreeningLog(): Promise<ScreeningEvent[]> {
  const res = await authenticatedFetch(`${BASE}/screening-log`, { cache: 'no-store' });
  const data = (await res.json()) as { events?: ScreeningEvent[] };
  return Array.isArray(data.events) ? data.events : [];
}

/** A shared URL — the shape recruiters can actually create. */
export interface CaseMaterialLink {
  kind: 'link';
  url: string;
  label: string;
  addedBy: string | null;
  addedAt: string;
}

/**
 * The original Firebase Storage upload. Kept so anything already stored still
 * reads, but it has never worked in production — see CaseMaterialRow.
 */
export interface CaseMaterialFile {
  kind?: undefined;
  filename: string;
  mimeType: string;
  bytes: number | null;
  storagePath: string;
  downloadUrl: string;
  uploadedBy: string | null;
  uploadedAt: string;
}

export type CaseMaterial = CaseMaterialLink | CaseMaterialFile;

/**
 * Attach or clear the role's case material (pass null to clear). Metadata only
 * — the file itself is uploaded to Firebase Storage by the browser.
 */
export async function saveCaseMaterial(
  postingId: string,
  material:
    | Omit<CaseMaterialLink, 'addedBy' | 'addedAt'>
    | Omit<CaseMaterialFile, 'uploadedBy' | 'uploadedAt'>
    | null
): Promise<CaseMaterial | null> {
  const res = await authenticatedFetch(`${BASE}/case-material`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postingId, material }),
  });
  const data = (await res.json()) as { panel?: { caseMaterial?: CaseMaterial | null } };
  return data.panel?.caseMaterial ?? null;
}

/**
 * Fetch stored case material and turn it into an email attachment.
 *
 * Done in the browser because the download URL is a Storage token URL the
 * client already holds; the proxy stays free of any Storage credential.
 */
export async function caseMaterialAsAttachment(
  material: CaseMaterialFile
): Promise<{ filename: string; mimeType: string; contentBase64: string; bytes: number }> {
  const res = await fetch(material.downloadUrl);
  if (!res.ok) throw new Error('Could not download the stored case material');
  const blob = await res.blob();
  const contentBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the case material'));
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.readAsDataURL(blob);
  });
  return {
    filename: material.filename,
    mimeType: material.mimeType,
    contentBase64,
    bytes: blob.size,
  };
}
